import {observer} from 'mobx-react';
import React from 'react';
import {X7PushableBlock} from '../../../domain';
import {Link, PageHeading, PromiseMessage, PromiseRetry} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {AddPushableBlockControls} from './parts/AddPushableBlockControls';
import {AddPushableBlockForm} from './parts/AddPushableBlockForm';
import {DeletePushableBlockControls} from './parts/DeletePushableBlockControls';
import {EditPushableBlockControls} from './parts/EditPushableBlockControls';
import {EditPushableBlockForm} from './parts/EditPushableBlockForm';
import {ReorderPushableBlocksControls} from './parts/ReorderPushableBlocksControls';
import {X6PageBreadcrumbs} from './X6PageBreadcrumbs';
import {X6PageStore} from './X6PageStore';
import {userStatusService} from '../../../domain'

function PushableBlockViewNoActions(pushableBlock: X7PushableBlock): React.ReactNode {
  return (
    <div className="row" key={'pushable-block-' + pushableBlock.Id}>
      <div className="col">
        <div className="text-box">
          {pushableBlock.Name}
        </div>
      </div>
    </div>
  );
}

function* ViewModeItems(store: X6PageStore): ReactNodeGenerator {
  const pushableBlocks = store.loader.pushableBlocks;

  if (pushableBlocks.length === 0)
    yield (
      <div className="row justify-content-center" key="no-pushable-blocks">
        <div className="col-auto">
          <div className="alert alert-info">
            No pushable blocks
          </div>
        </div>
      </div>);

  for (const pushableBlock of pushableBlocks) {
    yield (
      <div className="row" key={'pushable-block-' + pushableBlock.Id}>
        <div className="col">
          <div className="text-box">
            {pushableBlock.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <Link className="btn btn-primary"
                to={routes.x7pushableBlock(pushableBlock.Id)}> View </Link>
          {
            userStatusService.status.roles.ContentManager &&
            <>
              <button type="button" className="btn btn-primary"
                    onClick={() => store.beginEdit(pushableBlock.Id)}> Edit
              </button>
              <button type="button" className="btn btn-danger"
                      onClick={() => store.beginDelete(pushableBlock.Id)}> Delete
              </button>
            </>
          }
        </div>
      </div>);
  }
}

function* AddModeItems(store: X6PageStore): ReactNodeGenerator {
  const pushableBlocks = store.loader.pushableBlocks;

  for (const pushableBlock of pushableBlocks)
    yield PushableBlockViewNoActions(pushableBlock);

  yield (<AddPushableBlockForm key="add-pushable-block-form"
                               store={store}/>);

  yield (<AddPushableBlockControls key="add-pushable-block-controls"
                                   store={store}/>);
}

function* EditModeItems(store: X6PageStore): ReactNodeGenerator {
  const pushableBlocks = store.loader.pushableBlocks;
  const editPushableBlockId = store.editPushableBlock!.pushableBlock.Id;

  for (const pushableBlock of pushableBlocks) {
    if (pushableBlock.Id !== editPushableBlockId) {
      yield PushableBlockViewNoActions(pushableBlock);
      continue;
    }

    yield (<EditPushableBlockForm key="edit-pushable-block-form"
                                  store={store}/>);

    yield (<EditPushableBlockControls key="edit-pushable-block-controls"
                                      store={store}/>);
  }
}

function* DeleteModeItems(store: X6PageStore): ReactNodeGenerator {
  const pushableBlocks = store.loader.pushableBlocks;
  const deletePushableBlockId = store.deletePushableBlock!.pushableBlockId;

  for (let pushableBlock of pushableBlocks) {

    yield PushableBlockViewNoActions(pushableBlock);

    if (pushableBlock.Id !== deletePushableBlockId) {
      continue;
    }

    yield (<DeletePushableBlockControls key="delete-pushable-block-controls"
                                        store={store}/>);
  }
}

function* ReorderModeItems(store: X6PageStore): ReactNodeGenerator {
  const pushableBlockIds = store.reorder!.pushableBlockIds;
  const pushableBlockById = store.loader.pushableBlockById;

  for (let index = 0; index < pushableBlockIds.length; index++) {
    let pushableBlockId = pushableBlockIds[index];
    const page = pushableBlockById[pushableBlockId];
    const first = index === 0;
    const last = index === pushableBlockIds.length - 1;

    yield (
      <div className="row" key={'page-' + page.Id}>
        <div className="col">
          <div className="text-box">
            {page.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>);
  }

  yield (<ReorderPushableBlocksControls key="reorder-pushable-blocks-controls"
                                        store={store}/>);
}

function* cardBodyRenderer(store: X6PageStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'View':
      yield* ViewModeItems(store);
      break;
    case 'Edit':
      yield* EditModeItems(store);
      break;
    case 'Add':
      yield* AddModeItems(store);
      break;
    case 'Delete':
      yield* DeleteModeItems(store);
      break;
    case 'ReOrder':
      yield* ReorderModeItems(store);
      break;
  }
}

function* componentRenderer(store: X6PageStore): ReactNodeGenerator {
  const pageState = store.loader.state;
  const isPagePending = pageState?.isPending === true;
  const isPageFailure = pageState?.isRejected === true;
  const isPageSuccess = pageState?.isResolved === true;
  const page = store.loader.page;

  if (isPagePending || isPageFailure) {
    yield (<PageHeading key="heading"> Lesson </PageHeading>);

    yield(<PromiseMessage key="lesson-loader"
                          label="Loading lesson" state={pageState} align="center"/>);

    yield (<PromiseRetry key="lesson-retry"
                         state={pageState} retry={store.loader.load} align="center"/>);

    return;
  }

  if (!isPageSuccess || page === null)
    return;

  yield (<X6PageBreadcrumbs key="page-breadcrumbs" page={page}/>);

  yield (<PageHeading key="heading"> Page {page!.Name} </PageHeading>);

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col"> Pushable blocks</div>
              {
                store.mode === 'View' &&
                <div className="col-auto no-wrap">
                  {
                    userStatusService.status.roles.ContentManager &&
                    <>
                     <button type="button" className="btn btn-primary"
                            onClick={store.beginAdd}> Add
                    </button>
                    <button type="button" className="btn btn-primary"
                            disabled={store.loader.pushableBlocks.length < 2}
                            onClick={store.beginReorder}> Reorder
                    </button>
                    </>
                  }
                </div>
              }
            </div>
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  id: number;
}

@observer
export class X6PageComponent extends React.Component<Props> {
  readonly store: X6PageStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new X6PageStore(props.id);
  }

  componentDidMount() {
    this.store.loader.load();
  }

  render() {
    return Array.from(componentRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store.dispose();
  }
}
